import { AnyObject, DatabaseAdapterInterface } from '@chem-po/core'
import {
  INSURANCE_PROVIDERS,
  // AnyObject,
  InsuranceProvider,
  MIDWIVES,
  Practice,
  User,
  USER_INVITES,
  USER_ROLES,
  UserRoleItem,
  USERS,
} from '@hb/shared'
import { useEffect, useState } from 'react'
import { useFetchData } from '../../contexts/db/FetchDataContext'
// import { INSURANCE_PROVIDERS_REF, MIDWIVES_REF, USER_ROLES_REF } from '../firestoreCollections'

export interface UseCachedDoc<T extends AnyObject> {
  data: T | null
  loading: boolean
}

export interface CachedCollectionStoreData<T extends AnyObject> {
  subscribers: Record<string, Record<string, (data: T | null) => void>>
  snapshotListeners: Record<string, () => void>
  docs: Record<string, { data: T | null; isLoading: boolean }>
  subscribeToDoc: (id: string, callback: (data: T | null) => void) => () => void
}

const getUniqueSubscriberId = (existing: Array<string>) => {
  let id = Math.random().toString(36).substring(7)
  while (existing.includes(id)) {
    id = Math.random().toString(36).substring(7)
  }
  return id
}

const cached: Record<string, CachedCollectionStoreData<any>> = {}

const createCachedCollection = <T extends AnyObject>(
  subscribeToItem: DatabaseAdapterInterface['subscribeToItem'],
  path: string,
): CachedCollectionStoreData<T> => {
  const existing = cached[path]
  if (existing) return existing
  cached[path] = {
    subscribers: {},
    snapshotListeners: {},
    docs: {},
    subscribeToDoc: () => () => {},
  }

  const set = (
    data:
      | Partial<CachedCollectionStoreData<T>>
      | ((current: CachedCollectionStoreData<T>) => Partial<CachedCollectionStoreData<T>>),
  ) => {
    if (typeof data === 'function') {
      cached[path] = {
        ...cached[path],
        ...data(cached[path]),
      }
    }
    cached[path] = {
      ...cached[path],
      ...data,
    }
  }

  cached[path] = {
    ...cached[path],
    subscribeToDoc: (id, callback) => {
      const { snapshotListeners, subscribers } = cached[path]
      const subscriberId = getUniqueSubscriberId(Object.keys(subscribers))
      if (!snapshotListeners[id]) {
        const unsubscribe = subscribeToItem(
          `${path}/${id}`,
          fetched => {
            const data = (fetched ?? null) as T | null
            set(state => {
              const listeners = state.subscribers[id]
              if (listeners) {
                Object.values(listeners).forEach(listener => listener(data))
              }
              return {
                ...state,
                docs: {
                  ...state.docs,
                  [id]: { data, isLoading: false },
                },
              }
            })
          },
          error => {
            console.error(error)
          },
        )
        set(state => ({
          ...state,
          snapshotListeners: {
            ...state.snapshotListeners,
            [id]: unsubscribe,
          },
        }))
      }
      set(state => ({
        subscribers: {
          ...state.subscribers,
          [id]: {
            ...state.subscribers[id],
            [subscriberId]: callback,
          },
        },
      }))
      callback(cached[path].docs[id]?.data ?? null)
      return () => {
        set(state => {
          const { [subscriberId]: _, ...rest } = state.subscribers?.[id] ?? {}
          return {
            ...state,
            subscribers: {
              ...state.subscribers,
              [id]: rest,
            },
          }
        })
      }
    },
  }
  return cached[path]
}

const getCachedCollection = <T extends AnyObject>(
  subscribeToItem: DatabaseAdapterInterface['subscribeToItem'],
  path: string,
) => {
  const existing = cached[path]
  if (existing) return existing
  return createCachedCollection<T>(subscribeToItem, path)
}

export const useCachedDoc = <T extends AnyObject>(
  collectionPath: string,
  id: string | null,
): UseCachedDoc<T> => {
  const { subscribeToItem } = useFetchData()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<T | null>(null)
  // const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!id) {
      setLoading(false)
      setData(null)
      return () => {}
    }
    setLoading(true)
    return getCachedCollection<T>(subscribeToItem, collectionPath).subscribeToDoc(id, u => {
      setData(u as T | null)
      setLoading(false)
    })
  }, [collectionPath, id, subscribeToItem])

  return { data, loading }
}

export const getCachedDoc = (
  subscribeToItem: DatabaseAdapterInterface['subscribeToItem'],
  collectionPath: string,
  id: string,
) => {
  const cachedCollection = getCachedCollection(subscribeToItem, collectionPath)
  const cachedData = cachedCollection.docs[id]
  if (cachedData) return cachedData
  return {
    data: null,
    isLoading: true,
  }
}

export const useCachedUser = (id: string | null) => useCachedDoc<UserRoleItem>(USER_ROLES, id)
export const useCachedUserItem = (isInvite: boolean, id: string | null) =>
  useCachedDoc<User>(isInvite ? USER_INVITES : USERS, id)
export const useCachedPractice = (id: string | null) => useCachedDoc<Practice>(MIDWIVES, id)
export const useCachedInsurer = (id: string | null) =>
  useCachedDoc<InsuranceProvider>(INSURANCE_PROVIDERS, id)
export const useCachedPracticeUser = (practiceId: string, userId: string | null) =>
  useCachedDoc(`${MIDWIVES}/${practiceId}/${USER_ROLES}`, userId)
